export const NOTIFICATION_LIMIT = 5;

const normalizeText = value => String(value || '').toLowerCase();

const getCleanPublicUrl = publicUrl => {
  if (!publicUrl || publicUrl === '/') {
    return '';
  }

  return publicUrl.replace(/\/$/, '');
};

export const getNotificationPath = (activity = {}, publicUrl = '') => {
  const ticketNumber = activity.ticketNumber ? String(activity.ticketNumber).trim() : '';
  const baseUrl = getCleanPublicUrl(publicUrl);

  if (!ticketNumber) {
    return `${baseUrl}/dashboard/avis`;
  }

  return `${baseUrl}/dashboard/avis/${encodeURIComponent(ticketNumber)}`;
};

export const getNotificationVariant = (activity = {}) => {
  const type = normalizeText(`${activity.type} ${activity.statusLabel}`);

  if (type.includes('pay') || type.includes('paid') || type.includes('termine') || type.includes('complete')) {
    return 'success';
  }

  if (type.includes('modifi') || type.includes('update')) {
    return 'secondary';
  }

  if (type.includes('annul') || type.includes('cancel') || type.includes('error')) {
    return 'danger';
  }

  return 'primary';
};

export const buildNotificationItems = (activities = [], options = {}) => {
  const fallbackTitle = options.fallbackTitle || 'Activite recente';
  const limit = options.limit ?? NOTIFICATION_LIMIT;
  const publicUrl = options.publicUrl || '';

  return (Array.isArray(activities) ? activities : [])
    .filter(Boolean)
    .slice()
    .sort((left, right) => (Number(right.timestamp) || 0) - (Number(left.timestamp) || 0))
    .slice(0, limit)
    .map((activity, index) => {
      const ticketNumber = activity.ticketNumber ? String(activity.ticketNumber).trim() : '';
      const clientName = activity.clientName ? String(activity.clientName).trim() : '';
      const statusLabel = activity.statusLabel ? String(activity.statusLabel).trim() : '';
      const descriptionParts = [
        ticketNumber ? `Avis #${ticketNumber}` : '',
        clientName,
      ].filter(Boolean);

      return {
        id: activity.id || `${activity.type || 'activity'}-${ticketNumber || index}`,
        ticketNumber,
        title: activity.type || fallbackTitle,
        description: descriptionParts.join(' - ') || statusLabel,
        statusLabel,
        timestamp: activity.timestamp,
        fallbackTime: [activity.date, activity.time].filter(Boolean).join(' '),
        path: getNotificationPath(activity, publicUrl),
        variant: getNotificationVariant(activity),
      };
    });
};

export const sortNotificationsNewestFirst = (notifications = []) => (
  (Array.isArray(notifications) ? notifications : [])
    .filter(Boolean)
    .slice()
    .sort((left, right) => (
      (Number(right.timestamp) || 0) - (Number(left.timestamp) || 0)
      || String(left.id || '').localeCompare(String(right.id || ''))
    ))
);

export const getNotificationHeaderLabel = (count, translate) => {
  if (count === 0) {
    return translate('Dashboard_Notifications_HeaderNone');
  }

  if (count === 1) {
    return translate('Dashboard_Notifications_HeaderOne');
  }

  return translate('Dashboard_Notifications_HeaderMany', { count });
};

export const formatNotificationAge = (timestamp, now = Date.now(), translate) => {
  const numericTimestamp = Number(timestamp);

  if (!Number.isFinite(numericTimestamp) || numericTimestamp <= 0) {
    return '';
  }

  const elapsedMinutes = Math.floor(Math.max(0, now - numericTimestamp) / 60000);

  if (elapsedMinutes < 1) {
    return translate('Dashboard_Notifications_JustNow');
  }

  if (elapsedMinutes < 60) {
    return translate('Dashboard_Notifications_MinutesAgo', { count: elapsedMinutes });
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);

  if (elapsedHours < 24) {
    return translate('Dashboard_Notifications_HoursAgo', { count: elapsedHours });
  }

  return translate('Dashboard_Notifications_DaysAgo', { count: Math.floor(elapsedHours / 24) });
};
